import DemoNextSteps from '@/components/DemoNextSteps';
import { useState, useEffect, useRef } from 'react';
import { useDemoRequestForm } from '@/hooks/demo-req-form';
import Turnstile from './Turnstile';
import { trackEvent } from '@/lib/analytics';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

// The inner form mounts fresh for each opening, including after a successful request.
// eslint-disable-next-line react/prop-types
export default function DemoRequestDialog({ isOpen, onClose, initialMessage = '' }) {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Ücretsiz Demo Talebi</DialogTitle>
          <DialogDescription>
            Adınızı ve telefonunuzu bırakın. Sizi arayıp tanıtım için birlikte zaman belirleyelim.
          </DialogDescription>
        </DialogHeader>
        {isOpen && <DemoForm initialMessage={initialMessage} />}
      </DialogContent>
    </Dialog>
  );
}

// eslint-disable-next-line react/prop-types
function DemoForm({ initialMessage }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState(initialMessage);
  const [token, setToken] = useState(null);
  const [captchaCycle, setCaptchaCycle] = useState(0);
  const [validationError, setValidationError] = useState('');
  const started = useRef(false);
  const busy = useRef(false);
  const { submitDemoRequestForm, loading, error, success } = useDemoRequestForm();
  const track = (step, data = {}) => trackEvent('lead-form-' + step, { form: 'demo_dialog', ...data });
  useEffect(() => {
    trackEvent('lead-form-view', { form: 'demo_dialog' });
  }, []);
  const start = () => {
    if (!started.current) {
      started.current = true;
      track('start');
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy.current) return;
    track('attempt');
    setValidationError('');
    if (!name.trim() || !/^[+0-9 ()-]+$/.test(phone) || phone.replace(/\D/g, '').length < 10) {
      setValidationError('Adınızı ve geçerli telefon numaranızı girin.');
      track('error', { reason: 'validation' });
      return;
    }
    if (!token) {
      setValidationError('Güvenlik doğrulamasının tamamlanmasını bekleyin. Sorun sürerse doğrulamayı yeniden deneyin.');
      track('error', { reason: 'captcha_missing' });
      return;
    }
    busy.current = true;
    try {
      const result = await submitDemoRequestForm({
        name: name.trim(),
        phone: phone.trim(),
        message,
        recaptchaToken: token,
      });
      if (result.success) {
        track('success');
        trackEvent('demo-request-submit', { source: 'dialog' });
      } else track('error', { reason: 'request_failed' });
    } finally {
      busy.current = false;
      setToken(null);
      setCaptchaCycle((cycle) => cycle + 1);
    }
  };

  if (success)
    return (
      <div role="status" className="rounded-xl border border-green-200 bg-green-50 p-5 text-green-900">
        <h3 className="font-semibold">Talebiniz alındı!</h3>
        <p className="mt-2 text-sm leading-relaxed">
          Bıraktığınız telefon numarasından size ulaşıp tanıtım zamanını birlikte belirleyeceğiz.
        </p>
      </div>
    );

  return (
    <form
      onSubmit={submit}
      onChange={start}
      onInvalidCapture={() => track('error', { reason: 'validation' })}
      className="space-y-5"
    >
      <DemoNextSteps />
      <div>
        <label htmlFor="dialog-name" className="mb-2 block text-sm font-medium">
          Ad Soyad *
        </label>
        <input
          id="dialog-name"
          name="name"
          autoComplete="name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-xl border border-hairline px-4 py-3"
          placeholder="Adınız ve soyadınız"
        />
      </div>
      <div>
        <label htmlFor="dialog-phone" className="mb-2 block text-sm font-medium">
          Telefon *
        </label>
        <input
          id="dialog-phone"
          name="phone"
          type="tel"
          autoComplete="tel"
          required
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="w-full rounded-xl border border-hairline px-4 py-3"
          placeholder="+90 5XX XXX XX XX"
        />
      </div>
      <details open={initialMessage ? true : undefined} className="rounded-xl border border-hairline p-4">
        <summary className="cursor-pointer text-sm font-medium">Mesaj ve paket seçimi (isteğe bağlı)</summary>
        <label htmlFor="dialog-message" className="sr-only">
          Mesajınız
        </label>
        <textarea
          id="dialog-message"
          name="message"
          rows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="mt-3 w-full rounded-lg border border-hairline p-3 text-sm"
          placeholder="Eklemek istediğiniz bir not varsa yazın."
        />
      </details>
      <Turnstile
        key={captchaCycle}
        onVerify={(value) => {
          setToken(value);
          setValidationError('');
        }}
        onError={() => {
          setToken(null);
          track('error', { reason: 'captcha_load' });
        }}
        onExpire={() => setToken(null)}
      />
      {(validationError || error) && (
        <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
          {validationError || error}
        </p>
      )}
      <button
        type="submit"
        disabled={loading}
        aria-busy={loading}
        className="modern-btn-large w-full disabled:opacity-60"
      >
        {loading ? 'Gönderiliyor…' : 'Tanıtım talebimi gönder'}
      </button>
      <p className="text-center text-xs text-ink-muted">
        Bu form bir tanıtım talebidir; abonelik veya ödeme başlatmaz.
      </p>
    </form>
  );
}
